import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Anchor, ArrowLeft } from "lucide-react";

const CONTENT = {
  terms: {
    title: "Terms of Use",
    body: `Last updated: February 2026

1. ACCEPTANCE OF TERMS
By accessing CalmOceanPrep, you agree to these Terms of Use. If you do not agree, do not use the service.

2. SERVICE DESCRIPTION
CalmOceanPrep is an AI-powered oral examination simulator for Indian Merchant Navy Second Mate (F.G.) candidates. The service provides practice sessions and is NOT a replacement for official MMD preparation or an official examination.

3. ACCOUNT REGISTRATION
- You must provide a valid, permanent email address.
- You are responsible for maintaining the confidentiality of your account credentials.
- One account per person. Sharing accounts is prohibited.
- We reserve the right to suspend accounts suspected of abuse.

4. USAGE LIMITATIONS
- Free users receive 2 total practice sessions.
- All users are limited to 5 sessions per day.
- Each session consists of 10 questions from the AI surveyor.
- Misuse of the platform (off-topic usage, prompt injection, etc.) may result in session termination.

5. PAYMENT & SUBSCRIPTIONS
- Payments are processed through Razorpay in Indian Rupees (INR).
- Subscriptions auto-renew unless cancelled.
- You may cancel anytime from your Account page.

6. INTELLECTUAL PROPERTY
All content, including AI-generated questions and feedback, is the property of CalmOceanPrep.

7. DISCLAIMER
CalmOceanPrep does not guarantee exam success. The AI-generated content is for practice purposes only. Always refer to official study materials and syllabus.

8. LIMITATION OF LIABILITY
CalmOceanPrep shall not be liable for any damages arising from the use of the service.

9. CONTACT
For questions about these terms, contact us via WhatsApp: +91 7383870103`,
  },
  privacy: {
    title: "Privacy Policy",
    body: `Last updated: February 2026

1. INFORMATION WE COLLECT
- Email address (for account creation and communication)
- Password (stored in encrypted/hashed form)
- IP address (for security and abuse prevention)
- Browser fingerprint data (for fraud detection)
- Session data and chat messages (for providing the service)
- Payment information (processed by Razorpay; we do not store card details)

2. HOW WE USE YOUR INFORMATION
- To provide the oral exam simulation service
- To prevent abuse and fraud
- To process payments
- To send service-related emails (verification, password reset)
- To improve our service

3. DATA STORAGE
- Your data is stored securely in our database.
- Chat messages and session data are retained for your session history.
- We do not sell your data to third parties.

4. THIRD-PARTY SERVICES
- OpenAI: Processes your answers to generate AI examiner responses.
- Razorpay: Processes payments securely.
- Resend: Sends transactional emails.

5. DATA RETENTION
- Account data is retained while your account is active.
- You may request account deletion by contacting support.

6. YOUR RIGHTS
- Access your data through the session history feature.
- Request data deletion by contacting support.
- Opt out of non-essential communications.

7. CONTACT
For privacy concerns, contact us via WhatsApp: +91 7383870103`,
  },
  refund: {
    title: "Refund Policy",
    body: `Last updated: February 2026

1. SUBSCRIPTION CANCELLATION
- You may cancel your subscription at any time from your Account page.
- Upon cancellation, you retain access until the end of your current billing period.
- No partial refunds for unused time within a billing period.

2. REFUND ELIGIBILITY
- Refunds are considered on a case-by-case basis.
- If you experience technical issues that prevent you from using the service, you may be eligible for a refund.
- Refund requests must be made within 7 days of payment.

3. NON-REFUNDABLE
- Sessions that have been started and completed are not refundable.
- Free trial sessions are not refundable (they are free).
- Abuse or violation of Terms of Use is not eligible for refund.

4. HOW TO REQUEST A REFUND
- Contact us via WhatsApp: +91 7383870103
- Include your registered email address and payment details.
- We aim to respond within 48 hours.

5. REFUND PROCESSING
- Approved refunds are processed within 5-7 business days.
- Refunds are credited to the original payment method.`,
  },
};

export default function PolicyPage({ type = "terms" }) {
  const content = CONTENT[type] || CONTENT.terms;

  return (
    <div className="min-h-screen bg-white">
      <header className="cop-glass border-b sticky top-0 z-30" data-testid="policy-header">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl flex items-center gap-3 h-14">
          <Button variant="ghost" size="sm" className="p-1" asChild>
            <Link to="/"><ArrowLeft className="h-4 w-4" /></Link>
          </Button>
          <Anchor className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">{content.title}</span>
        </div>
      </header>
      <main className="container mx-auto px-4 md:px-6 max-w-3xl py-8" data-testid="policy-content">
        <h1 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Manrope' }}>{content.title}</h1>
        <div className="prose prose-sm max-w-none">
          {content.body.split('\n').map((line, i) => (
            <p key={i} className={`${line.match(/^\d+\./) ? 'font-semibold mt-4 mb-1' : 'text-muted-foreground mb-2'} text-sm leading-relaxed`}>
              {line}
            </p>
          ))}
        </div>
      </main>
    </div>
  );
}
